
import {Component, Vue} from "vue-property-decorator";
import {TRANSLATIONS_FETCH} from "@/store/ActionTypes";
import {mapState} from "vuex";
import DashboardRepository from "@/utils/API/DashboardRepository";
import ListRequest, {OrderType} from "@/models/ListRequest";
import {Recipe} from "@/models/Recipes/Recipe";
import {Dish} from "@/models/Dishes/Dish";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vTable from "@/components/vTable.vue";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import {
  debounce,
  getPreviewNutrientsForCountry,
  mapNutritions,
  tableMapper,
  parseISOString,
  millisToDays, round
} from "@/utils/Helpers";
import DishesRepository from "@/utils/API/DishesRepository";
import IngredientsRepository from "@/utils/API/IngredientsRepository";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import type { Ingredient } from "@/models/Ingredient/Ingredient";
import { NutrientType } from "@/models/Ingredient/NutrientType";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import VModal from "@/components/vModal.vue";
import {AccountState} from "@/store/modules/AccountModule";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {useStoreState} from "@/hooks/useStoreState";
import { UserTypes } from "@/models/User/UserTypes";
import RestaurantRepository from "@/utils/API/RestaurantRepository";
import { TrialRestaurantActivity } from "@/models/Restaurants/TrialRestaurantActivity";
import { TrialRestaurantMetadataDto } from "@/models/Restaurants/TrialRestaurantMetadataDto";
import { BookDemoRequestDto } from "@/models/Restaurants/BookDemoRequestDto";
import VBookDemoRequestModal from "@/components/modals/marketing/vBookDemoRequestModal.vue";
import WorkspaceActionCards from "@/components/WorkspaceActionCards.vue";
import RegulatoryNotificationRepository, { type RegulatoryNotificationItem } from "@/utils/API/RegulatoryNotificationRepository";
import {
  loadDashboardOverviewCache,
  saveDashboardOverviewCache,
} from "@/utils/dashboardCache";

/**
 * Chart.js instance
 * https://www.chartjs.org/docs/latest/
 */
const Chart = require("chart.js");
/**
 * numscroller instance
 */
const numscroller = require("@/assets/js/numscroller-1.0.js");
const storeState = useStoreState();
/**
 * Model for storing totals state used by chart and counter widget
 */
interface Totals {
  totalRecipes: number;
  totalDishes: number;
  totalIngredients: number;
  chartData: any;
}

/**
 * Model for storing recipes state for the dashboard.
 */
interface RecentRecipes {
  listRequest: ListRequest;
  recipes: Recipe[];
  loading: boolean;
  searchString: string;
  hasRecipes: boolean;
}

/**
 * Model for storing dishes state for the dashboard.
 */
interface RecentDishes {
  listRequest: ListRequest;
  dishes: Dish[];
  loading: boolean;
  searchString: string;
  hasDishes: boolean;
}

interface CustomIngredientSummaryState {
  ingredients: Ingredient[];
  loading: boolean;
}

interface DashboardOverviewState {
  counts?: {
    totalRecipes: number;
    totalCombinedRecipes: number;
    totalCustomIngredients: number;
  };
  labelMaker?: {
    available: number | null;
    downloaded: number;
    remaining: number | null;
    percentUsed: number;
  };
  ingredientUsage?: Array<{
    name: string;
    count: number;
  }>;
  allergenCoverage?: Array<{
    label: string;
    total: number;
    analyzed: number;
    contained: number;
    pendingAnalysis: number;
  }>;
  caloriesBreakdown?: {
    recipes: Array<{ label: string; value: number }>;
    combinedRecipes: Array<{ label: string; value: number }>;
    customIngredients: Array<{ label: string; value: number }>;
  };
}

type RegulatoryCard = {
  key: string;
  country: string;
  hasNewUpdates: boolean;
  updates: Array<{
    title: string;
    summary: string;
    link: string;
    isNew: boolean;
  }>;
};

type HomepageFeatureItem = {
  id: number;
  title: string;
  description: string;
  link: string;
  linkLabel: string;
  icon: string;
};

type TutorialConfig = {
  title: string;
  heading: string;
  description: string;
  subtext: string;
  embedSrc: string;
  embedTitle: string;
  ctaLabel?: string;
  ctaRouteName?: string;
  redirectOnCloseName?: string;
  redirectOnCloseParams?: Record<string, string>;
  redirectOnCloseQuery?: Record<string, string>;
  useDemoEmbedLayout?: boolean;
};

const defaultTutorialConfig: TutorialConfig = {
  title: "",
  heading: "",
  description: "",
  subtext: "",
  embedSrc: "",
  embedTitle: "",
  ctaLabel: undefined,
  ctaRouteName: undefined,
  redirectOnCloseName: undefined,
  redirectOnCloseParams: undefined,
  redirectOnCloseQuery: undefined,
  useDemoEmbedLayout: false,
};

@Component({
  components: {
    VBookDemoRequestModal,
    VUpsellModal,
    VModal,
    WorkspaceActionCards,
    vTable,
  },
  methods: {
    millisToDays,
    parseISOString
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class Home extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;
  trialMetadata: TrialRestaurantMetadataDto | null = null;
  isUpdatingWalkthrough = false;
  allergenSummary = {
    total: 0,
    analyzed: 0,
    contained: 0,
  };
  allergenDishSummary = {
    total: 0,
    analyzed: 0,
    contained: 0,
  };

  totals: Totals = {
    totalRecipes: 0,
    totalIngredients: 0,
    totalDishes: 0,
    chartData: 0,
  };
  recentRecipes: RecentRecipes = {
    listRequest: new ListRequest(
      {
        page: 1,
        pageSize: 5,
        orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
        filters: [{ name: "Name", value: "" }],
      },
      undefined,
      getPreviewNutrientsForCountry(NutritionLabelCountry.Canada)
    ),
    recipes: [],
    loading: true,
    searchString: "",
    hasRecipes: false,
  };
  recentDishes: RecentDishes = {
    listRequest: new ListRequest(
      {
        page: 1,
        pageSize: 5,
        orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
        filters: [{ name: "Name", value: "" }],
      },
      undefined,
      getPreviewNutrientsForCountry(NutritionLabelCountry.Canada)
    ),
    dishes: [],
    loading: true,
    searchString: "",
    hasDishes: false,
  };
  customIngredientSummary: CustomIngredientSummaryState = {
    ingredients: [],
    loading: false,
  };

  showTrialUpsellModal: boolean = false;
  showTutorialModal: boolean = false;
  isTutorialEmbedLoading: boolean = false;
  activeTutorial: TutorialConfig = { ...defaultTutorialConfig };
  showBookDemoModal = false;
  isSendingDemoRequest = false;
  regulatoryItems: RegulatoryNotificationItem[] = [];
  featureItems: RegulatoryNotificationItem[] = [];
  regulatoryCarouselStart = 0;
  dashboardOverview: DashboardOverviewState | null = null;

  get trialMilestones() {
    return [
      {
        key: "createdFirstRecipe",
        label: "Create your first recipe",
        completed: this.trialMetadata?.hasCreatedFirstRecipe ?? false,
      },
      {
        key: "addedIngredients",
        label: "Add ingredients",
        completed: this.trialMetadata?.hasTriedAddingIngredientToFirstRecipe ?? false,
      },
      {
        key: "generatedFirstLabel",
        label: "Generate your first label",
        completed: this.trialMetadata?.hasGeneratedFirstLabel ?? false,
      },
      {
        key: "downloadedFirstLabel",
        label: "Download your label",
        completed: this.trialMetadata?.hasDownloadedFirstLabel ?? false,
      },
      {
        key: "bookedWalkthrough",
        label: "Book a free 15-min walkthrough",
        completed: this.trialMetadata?.hasBookedFreeFifteenMinuteDemo ?? false,
      },
    ];
  }

  get quickAccessCards() {
    return [
      {
        key: "recipes",
        title: "Recipes",
        description: "Enter your recipes and its ingredients to generate a nutritional analysis for each recipe. Create nutrition labels to print or save.",
        countValue: this.totals.totalRecipes,
        icon: "fa fa-book",
        tone: "recipe",
        actionLabel: "View recipes",
        tutorialLabel: "View tutorial",
      },
      {
        key: "dishes",
        title: "Combined Recipes",
        description: "Combine your recipes to create combined recipes and generate a nutritional analysis for each. Create nutrition labels for print or save.",
        countValue: this.totals.totalDishes,
        icon: "fa fa-cutlery",
        tone: "combined",
        actionLabel: "View combined recipes",
        tutorialLabel: "View tutorial",
      },
      {
        key: "ingredients",
        title: "Custom Ingredients",
        description: "Create and manage custom ingredients for your recipes. Add ingredient details and use them across your recipe calculations.",
        countValue: this.totals.totalIngredients,
        icon: "fa fa-spoon",
        tone: "ingredient",
        actionLabel: "View custom ingredients",
        tutorialLabel: "View tutorial",
      },
    ];
  }

  get regulatoryCards(): RegulatoryCard[] {
    const groupedItems = new Map<string, RegulatoryNotificationItem[]>();

    this.regulatoryItems
      .filter((item) => item.type === "Regulatory" && item.isVisible)
      .forEach((item) => {
        const key = String(item.countryId ?? item.countryName ?? item.id);
        const existingItems = groupedItems.get(key) || [];
        existingItems.push(item);
        groupedItems.set(key, existingItems);
      });

    return Array.from(groupedItems.entries()).map(([key, items]) => {
      const visibleUpdates = items
        .sort((left, right) => left.displayOrder - right.displayOrder || right.id - left.id)
        .slice(0, 3);

      return {
        key,
        country: visibleUpdates[0]?.countryName || "Regulatory",
        hasNewUpdates: visibleUpdates.some((item) => item.isNew),
        updates: visibleUpdates.map((item) => ({
          title: item.title,
          summary: item.summary,
          link: item.url,
          isNew: item.isNew,
        })),
      };
    });
  }

  get hasRegulatoryCarousel() {
    return this.regulatoryCards.length > 3;
  }

  get maxRegulatoryCarouselStart() {
    return Math.max(this.regulatoryCards.length - 3, 0);
  }

  get normalizedRegulatoryCarouselStart() {
    return Math.min(this.regulatoryCarouselStart, this.maxRegulatoryCarouselStart);
  }

  get visibleRegulatoryCards() {
    if (!this.hasRegulatoryCarousel) {
      return this.regulatoryCards;
    }

    return this.regulatoryCards.slice(
      this.normalizedRegulatoryCarouselStart,
      this.normalizedRegulatoryCarouselStart + 3
    );
  }

  get canSlideRegulatoryLeft() {
    return this.normalizedRegulatoryCarouselStart > 0;
  }

  get canSlideRegulatoryRight() {
    return this.normalizedRegulatoryCarouselStart < this.maxRegulatoryCarouselStart;
  }

  slideRegulatoryLeft() {
    if (!this.canSlideRegulatoryLeft) {
      return;
    }

    this.regulatoryCarouselStart = this.normalizedRegulatoryCarouselStart - 1;
  }

  slideRegulatoryRight() {
    if (!this.canSlideRegulatoryRight) {
      return;
    }

    this.regulatoryCarouselStart = this.normalizedRegulatoryCarouselStart + 1;
  }

  get completedTrialMilestones() {
    return this.trialMilestones.filter((item) => item.completed).length;
  }

  get totalTrialMilestones() {
    return this.trialMilestones.length;
  }

  get trialMilestoneProgressPercent() {
    if (this.totalTrialMilestones === 0) {
      return 0;
    }

    return (this.completedTrialMilestones / this.totalTrialMilestones) * 100;
  }

  get hasBookedWalkthrough() {
    return this.trialMetadata?.hasBookedFreeFifteenMinuteDemo ?? false;
  }

  get demoRequestDefaultName() {
    const firstName = this.accountState.userDetails.firstName || "";
    const lastName = this.accountState.userDetails.lastName || "";
    return `${firstName} ${lastName}`.trim();
  }

  get demoRequestDefaultEmail() {
    return this.accountState.userDetails.email || "";
  }

  get demoRequestDefaultCompanyName() {
    // UserDetails.restaurantName is protected in the model, but value exists on API payload.
    return (this.accountState.userDetails as any).restaurantName || "";
  }

  get demoRequestDefaultPhone() {
    // UserDetails.phone is protected in the model, but value exists on API payload.
    return (this.accountState.userDetails as any).phone || "";
  }

  editDish(id: number) {
    this.$router.push({ name: "dishesEdit", params: { id: id.toString() } });
  }

  editRecipe(id: number) {
    this.$router.push({ name: "recipesEdit", params: { id: id.toString() } });
  }

  /**
   * Debounced method for handling search recipes on user input.
   */
  searchRecipes() {
    if (this.recentRecipes.listRequest.queryOptions.filters) {
      this.recentRecipes.listRequest.queryOptions.filters.length = 0;
      this.recentRecipes.listRequest.queryOptions.filters.push({
        name: "Name",
        value: this.recentRecipes.searchString,
      });
    }
    debounce(
      () => {
        this.requestRecipes();
      },
      250,
      "request-recipes"
    );
  }

  // Show recipe cost link, check if user paid for recipe cost
  showRecipeCostLink() {
    if (this.accountState.userDetails.addOns) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.RecipeCost);
    }
    return false;
  }

  handleRecipeCostButton() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
    }

    else {
      this.$router.push({ name: 'recipesCost' });
    }
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
  }

  openTutorialModal(tutorial: TutorialConfig) {
    this.activeTutorial = { ...tutorial };
    this.isTutorialEmbedLoading = true;
    this.showTutorialModal = true;
  }

  closeTutorialModal() {
    const {
      redirectOnCloseName,
      redirectOnCloseParams,
      redirectOnCloseQuery,
    } = this.activeTutorial;

    this.showTutorialModal = false;
    this.isTutorialEmbedLoading = false;
    this.activeTutorial = { ...defaultTutorialConfig };

    if (redirectOnCloseName) {
      this.$router.push({
        name: redirectOnCloseName,
        params: redirectOnCloseParams,
        query: redirectOnCloseQuery,
      });
    }
  }

  handleTutorialIframeLoad() {
    this.isTutorialEmbedLoading = false;
  }

  handleTutorialCta() {
    const { ctaRouteName } = this.activeTutorial;

    this.closeTutorialModal();

    if (ctaRouteName) {
      this.$router.push({ name: ctaRouteName });
    }
  }

  openCustomIngredientsTutorial() {
    this.$router.push({ name: "trainingVideos" });
  }

  handleQuickAccessAction(key: string) {
    if (key === "recipes") {
      this.$router.push({ name: "recipes" });
      return;
    }

    if (key === "dishes") {
      this.$router.push({ name: "dishes" });
      return;
    }

    if (key === "ingredients") {
      this.$router.push({ name: "ingredients" });
    }
  }

  handleQuickAccessTutorial(key: string) {
    if (key === "recipes") {
      this.openTutorialModal(this.recipesTutorial);
      return;
    }

    if (key === "dishes") {
      this.openTutorialModal(this.combinedRecipesTutorial);
      return;
    }

    if (key === "ingredients") {
      this.openCustomIngredientsTutorial();
    }
  }

  handleHomepageFeatureAction(feature: any) {
    if (feature.link) {
      window.open(feature.link, "_blank");
    }
  }

  handleStartupFeatureQuery() {
    this.$nextTick(() => {
      if (this.$route.query.openNotifications === "1") {
        window.dispatchEvent(new CustomEvent("menusano:open-notifications"));
      }

      if (this.$route.query.focusRegulatory === "1") {
        const container = document.querySelector(".page-content-wrapper");
        const regulatorySection = document.querySelector(".dash-top-row");

        if (container instanceof HTMLElement && regulatorySection instanceof HTMLElement) {
          container.scrollTo({
            top: Math.max(regulatorySection.offsetTop - 24, 0),
            behavior: "smooth",
          });
          return;
        }

        regulatorySection?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }

  clearStartupFeatureQuery() {
    if (this.$route.query.openNotifications !== "1" && this.$route.query.focusRegulatory !== "1") {
      return;
    }

    const nextQuery = { ...this.$route.query } as Record<string, any>;
    delete nextQuery.openNotifications;
    delete nextQuery.focusRegulatory;

    this.$router.replace({
      name: this.$route.name || undefined,
      params: this.$route.params,
      query: nextQuery,
    }).catch((): void => undefined);
  }

  async loadTrialMetadata() {
    if (!this.accountState.userDetails.isTrial) {
      return;
    }

    try {
      const response = await RestaurantRepository.getCurrentTrialMetadata();
      this.trialMetadata = response.data;
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(
          error.response.data?.errorMessages?.[0]?.userVisibleMessage ||
            error.response.data?.message
        );
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
  }

  async bookFreeWalkthrough() {
    if (this.hasBookedWalkthrough) {
      this.$snotify.error("Your demo request has already been sent.");
      return;
    }

    this.openBookDemoModal();
  }

  openBookDemoModal() {
    this.showBookDemoModal = true;
  }

  closeBookDemoModal() {
    if (this.isSendingDemoRequest) {
      return;
    }

    this.showBookDemoModal = false;
  }

  async sendBookDemoRequest(payload: BookDemoRequestDto) {
    if (this.isSendingDemoRequest) {
      return;
    }

    this.isSendingDemoRequest = true;

    try {
      await RestaurantRepository.bookDemoRequest(payload);

      if (this.accountState.userDetails.isTrial && !this.hasBookedWalkthrough) {
        try {
          const response = await RestaurantRepository.updateCurrentTrialActivity({
            activity: TrialRestaurantActivity.BookedFreeFifteenMinuteDemo,
          });
          this.trialMetadata = response.data;
        }
        catch {
          // Keep demo submission successful even if trial-activity sync fails.
        }
      }

      this.$snotify.success("Your demo request has been sent.");
      this.closeBookDemoModal();
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(
          error.response.data?.errorMessages?.[0]?.userVisibleMessage ||
            error.response.data?.message
        );
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
    finally {
      this.isSendingDemoRequest = false;
      this.showBookDemoModal = false;
    }
  }

  trackTrainingVideoView() {
    if (!this.accountState.userDetails.isTrial) {
      return;
    }

    void RestaurantRepository.updateCurrentTrialActivity({
      activity: TrialRestaurantActivity.ViewedTrainingVideo,
    })
      .then((response) => {
        this.trialMetadata = response.data;
      })
      .catch(() => {
        // Demo playback should not be blocked if activity tracking fails.
      });
  }

  viewTrainingVideo() {
    this.openTutorialModal(this.homeTrainingDemoTutorial);
    this.trackTrainingVideoView();
  }

  get exportLabelReturnRoute() {
    const mostRecentRecipe = this.recentRecipes.recipes[0];

    if (mostRecentRecipe?.id) {
      return {
        name: "recipesEdit",
        params: { id: mostRecentRecipe.id.toString() },
        query: { openExportLabel: "1" },
      };
    }

    return {
      name: "recipesAdd",
    };
  }

  get homeTrainingDemoTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("home_training_demo_title", "MenuSano Overview"),
      heading: this.translationOrFallback("home_training_demo_heading", "Watch how to navigate MenuSano and get familiar with the main workflow"),
      description: "",
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmokasft48kmaza2iswac68lw?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("home_training_demo_title", "MenuSano Overview"),
      ctaLabel: this.translationOrFallback("home_training_demo_cta", "See all tutorials"),
      ctaRouteName: "trainingVideos",
      useDemoEmbedLayout: true,
    };
  }

  /**
   * Calculates the number of days remaining in the user's free trial period.
   *
   * @return {number} The number of days remaining until the subscription end date, rounded to the nearest whole number. Returns 0 if the free trial has already ended.
   */
  getFreeTrialDaysRemaining(): number {
    return round(
      millisToDays(
        Math.abs(
          parseISOString(this.accountState.userDetails.subscriptionEndDate).getTime() - new Date().getTime()
        )
      ),
      0
    );
  }

  /**
   * Debounced method for handling search dishes on user input.
   */
  searchDishes() {
    if (this.recentDishes.listRequest.queryOptions.filters) {
      this.recentDishes.listRequest.queryOptions.filters.length = 0;
      this.recentDishes.listRequest.queryOptions.filters.push({
        name: "Name",
        value: this.recentDishes.searchString,
      });
    }
    debounce
      (() => {
         this.requestDishes();
       },
       250,
       "request-dishes"
      );
  }

  /**
   * Getter method to transform the list of recipes to grid data (vTable)
   */
  get transformRecipe() {
    return this.recentRecipes.recipes.map((x) => tableMapper(x, true));
  }

  /**
   * Getter method to transform the list of dishes to grid data (vTable)
   */
  get transformDish() {
    return this.recentDishes.dishes.map((x) => tableMapper(x, true));
  }

  get displayLabelLimits() {
    const userDetails = this.accountState?.userDetails;
    return !!userDetails && userDetails.nutritionLabelsLimit?.labelsLimit != null;
  }

  translationOrFallback(key: string, fallback: string) {
    const translation = this.translations.get(key);
    return !translation || translation === "---" ? fallback : translation;
  }

  getRegulatoryUpdatesLabel(count: number) {
    const label = count === 1
      ? this.translationOrFallback("dashboard_regulatory_update_singular", "regulatory update")
      : this.translationOrFallback("dashboard_regulatory_update_plural", "regulatory updates");

    return `${count} ${label}`;
  }

  getRecipeCountLabel(count: number) {
    return count === 1
      ? this.translationOrFallback("wellness_tab_recipe", "recipe")
      : this.translationOrFallback("dashboard_recipes", "recipes");
  }

  translateDashboardLabel(label: string) {
    switch (label) {
    case "Recipes":
      return this.translationOrFallback("dashboard_recipes", "Recipes");
    case "Combined Recipes":
      return this.translationOrFallback("rec_dishes_title", "Combined Recipes");
    case "Custom Ingredients":
      return this.translationOrFallback("wellness_tab_custom_ingredients", "Custom Ingredients");
    default:
      return label;
    }
  }

  get recipesTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("recipes", "Recipes"),
      heading: this.translationOrFallback("home_recipes_tutorial_heading", "See how to create a recipe directly inside MenuSano"),
      description: this.translationOrFallback("home_recipes_tutorial_description", "Walk through the recipe creation flow without leaving the app so users can learn the core steps right from the homepage."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmluzqsi83yl5d2nt6tzik8ql?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("home_recipes_tutorial_embed_title", "How to create a recipe"),
    };
  }

  get combinedRecipesTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("rec_dishes_title", "Combined Recipes"),
      heading: this.translationOrFallback("home_combined_recipes_tutorial_heading", "See how Combined Recipes are created and managed inside MenuSano"),
      description: this.translationOrFallback("home_combined_recipes_tutorial_description", "Preview the combined recipe workflow in-app so users can follow the demo and return straight back to the homepage when finished."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmn3eqr506bx7sjdo5c6c0us3?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("home_combined_recipes_tutorial_embed_title", "Menusano Demo combined recipe new label"),
    };
  }

  get recipeCostsTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("tutorial_recipe_costs_title", "Recipe Costs"),
      heading: this.translationOrFallback("tutorial_recipe_costs_heading", "See how Recipe Costs turns recipe data into a clearer costing workflow for your team"),
      description: this.translationOrFallback("tutorial_recipe_costs_description", "Explore how the Recipe Cost add-on brings ingredient pricing and recipe composition together so you can review recipe-level cost details faster."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmnoxdv237iznaburdt40j2z7?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("tutorial_recipe_costs_embed_title", "Menusano Recipe costing"),
    };
  }

  get activeTutorialEmbedStyle() {
    if (this.activeTutorial.useDemoEmbedLayout) {
      return {
        position: "relative",
        boxSizing: "content-box",
        maxHeight: "68vh",
        width: "100%",
        aspectRatio: "1.96",
        padding: "24px 0 24px 0",
      };
    }

    return {
      position: "relative",
      boxSizing: "content-box",
      maxWidth: "100%",
      width: "100%",
      aspectRatio: "1.82",
      margin: "0 auto",
      padding: "0",
    };
  }

  get labelLimit(){
    return this.dashboardOverview?.labelMaker?.available
      ?? storeState.value.AccountModule.userDetails.nutritionLabelsLimit?.labelsLimit
      ?? 0;
  }

  get downloadedLabels() {
    return this.dashboardOverview?.labelMaker?.downloaded
      ?? storeState.value.AccountModule.userDetails.nutritionLabelsLimit?.downloadedLabels
      ?? 0;
  }

  get paidLabelsAvailable() {
    return storeState.value.AccountModule.userDetails.nutritionLabelsLimit?.paidLabelsAvailable ?? 0;
  }

  get currentPackageName() {
    return (this.accountState?.userDetails as any)?.subscriptionName || "";
  }

  get isSpecialAccount() {
    return storeState.value.AccountModule.userDetails.isSpecial === true;
  }

  get isOwner() {
    return this.accountState.userDetails.userType === UserTypes.Owner;
  }

  get remainingLabels() {
    if (this.dashboardOverview?.labelMaker?.remaining != null) {
      return this.dashboardOverview.labelMaker.remaining;
    }

    return Math.max(this.labelLimit - this.downloadedLabels, 0);
  }

  get labelMakerUsagePercent() {
    if (this.dashboardOverview?.labelMaker?.percentUsed != null) {
      return this.dashboardOverview.labelMaker.percentUsed;
    }

    if (!this.labelLimit) {
      return 0;
    }

    return Math.min(Math.round((this.downloadedLabels / this.labelLimit) * 100), 100);
  }

  get labelMakerChartStyle() {
    const usedPercent = this.labelMakerUsagePercent;
    return {
      background: `conic-gradient(#4d8f58 0deg ${usedPercent * 3.6}deg, #dfe9df ${usedPercent * 3.6}deg 360deg)`,
    };
  }

  get allergenRecipeTotal() {
    return this.allergenSummary.total || this.totals.totalRecipes || 0;
  }

  get allergenContainedPercent() {
    if (!this.allergenRecipeTotal) {
      return 0;
    }

    return Math.round((this.allergenSummary.contained / this.allergenRecipeTotal) * 100);
  }

  handleBuyMoreLabelsClick() {
    if (!this.isOwner) {
      this.$snotify.error("Only the account owner can purchase add-on labels.");
      return;
    }

    this.$router.push({ name: "subscription", query: { buyLabels: "1" } }).catch(() => {});
  }

  get allergenRecipeChartStyle() {
    const total = this.allergenRecipeTotal;
    if (!total) {
      return {
        background: "#eef2f5",
      };
    }

    const containedDegrees = (this.allergenSummary.contained / total) * 360;
    const analyzedOnlyDegrees = (Math.max(this.allergenSummary.analyzed - this.allergenSummary.contained, 0) / total) * 360;
    const pendingDegrees = Math.max(360 - containedDegrees - analyzedOnlyDegrees, 0);

    return {
      background: `conic-gradient(#6aa84f 0deg ${containedDegrees}deg, #9ec7de ${containedDegrees}deg ${containedDegrees + analyzedOnlyDegrees}deg, #e7edf1 ${containedDegrees + analyzedOnlyDegrees}deg ${containedDegrees + analyzedOnlyDegrees + pendingDegrees}deg)`,
    };
  }

  get allergenCoverageItems() {
    const overviewItems = this.dashboardOverview?.allergenCoverage || [];
    if (overviewItems.length) {
      return overviewItems.map((item) => ({
        ...item,
        percent: item.total ? Math.round((item.contained / item.total) * 100) : 0,
        analyzedOnly: Math.max(item.analyzed - item.contained, 0),
        remaining: item.pendingAnalysis,
        containedPercent: item.total ? Math.round((item.contained / item.total) * 100) : 0,
        analyzedOnlyPercent: item.total ? Math.round((Math.max(item.analyzed - item.contained, 0) / item.total) * 100) : 0,
        remainingPercent: item.total ? Math.max(100 - Math.round((item.analyzed / item.total) * 100), 0) : 0,
      }));
    }

    const items = [
      {
        label: "Recipes",
        total: this.allergenSummary.total || this.totals.totalRecipes || 0,
        analyzed: this.allergenSummary.analyzed,
        contained: this.allergenSummary.contained,
      },
      {
        label: "Combined Recipes",
        total: this.allergenDishSummary.total || this.totals.totalDishes || 0,
        analyzed: this.allergenDishSummary.analyzed,
        contained: this.allergenDishSummary.contained,
      },
      {
        label: "Custom Ingredients",
        total: this.totals.totalIngredients || 0,
        analyzed: this.customIngredientSummary.ingredients.filter((item) => item.hasAllergenAnalysis).length,
        contained: this.customIngredientSummary.ingredients.filter((item) => item.hasAllergenAnalysis && item.hasAllergen).length,
      },
    ];

    return items.map((item) => ({
      ...item,
      percent: item.total ? Math.round((item.contained / item.total) * 100) : 0,
      analyzedOnly: Math.max(item.analyzed - item.contained, 0),
      remaining: Math.max(item.total - item.analyzed, 0),
      containedPercent: item.total ? Math.round((item.contained / item.total) * 100) : 0,
      analyzedOnlyPercent: item.total ? Math.round((Math.max(item.analyzed - item.contained, 0) / item.total) * 100) : 0,
      remainingPercent: item.total ? Math.max(100 - Math.round((item.analyzed / item.total) * 100), 0) : 0,
    }));
  }

  get homepageFeatureItems(): HomepageFeatureItem[] {
    return this.featureItems
      .sort((left, right) => {
        const leftOrder = left.displayOrder > 0 ? left.displayOrder : Number.MAX_SAFE_INTEGER;
        const rightOrder = right.displayOrder > 0 ? right.displayOrder : Number.MAX_SAFE_INTEGER;
        return leftOrder - rightOrder || right.id - left.id;
      })
      .slice(0, 3)
      .map((item) => ({
        id: item.id,
        title: item.title,
        description: item.summary,
        link: item.url,
        linkLabel: this.translationOrFallback("dashboard_feature_open_cta", "Open feature"),
        icon: "fa fa-lightbulb-o",
      }));
  }

  get recipesCalorieSegments() {
    const segments = this.dashboardOverview?.caloriesBreakdown?.recipes || [
      { label: "Under 250 cal", value: this.totals.chartData?.lowCaloriesRecipes || 0 },
      { label: "250 to 500 cal", value: this.totals.chartData?.moderateCaloriesRecipes || 0 },
      { label: "501 to 1000 cal", value: this.totals.chartData?.highCaloriesRecipes || 0 },
      { label: "1000+ cal", value: this.totals.chartData?.overThousandCaloriesRecipes || 0 },
    ];
    const total = this.totals.totalRecipes || 0;
    if (!total || !segments.length) {
      return [];
    }

    return this.buildPercentageSegments(segments, total);
  }

  get dishesCalorieSegments() {
    const segments = this.dashboardOverview?.caloriesBreakdown?.combinedRecipes || [
      { label: "Under 500 cal", value: this.totals.chartData?.lowCaloriesDishes || 0 },
      { label: "501 to 750 cal", value: this.totals.chartData?.moderateCaloriesDishes || 0 },
      { label: "751 to 1000 cal", value: this.totals.chartData?.highCaloriesDishes || 0 },
      { label: "1000+ cal", value: this.totals.chartData?.overThousandCaloriesDishes || 0 },
    ];
    const total = this.totals.totalDishes || 0;
    if (!total || !segments.length) {
      return [];
    }

    return this.buildPercentageSegments(segments, total);
  }

  get customIngredientCalorieSegments() {
    const segments = [
      {
        label: "Under 250 cal",
        count: 0,
        value: 0,
      },
      {
        label: "250 to 500 cal",
        count: 0,
        value: 0,
      },
      {
        label: "501 to 1000 cal",
        count: 0,
        value: 0,
      },
      {
        label: "1000+ cal",
        count: 0,
        value: 0,
      },
    ];

    const overviewSegments = this.dashboardOverview?.caloriesBreakdown?.customIngredients || [];
    if (overviewSegments.length) {
      const total = this.totals.totalIngredients || 0;
      const segmentMap = new Map(overviewSegments.map((segment) => [segment.label, segment.value]));
      const normalizedSegments = segments.map((segment) => ({
        label: segment.label,
        value: segmentMap.get(segment.label) || 0,
      }));

      if (!total) {
        return normalizedSegments.map((segment) => ({
          ...segment,
          percent: 0,
        }));
      }

      return this.buildPercentageSegments(normalizedSegments, total);
    }

    const ingredients = this.customIngredientSummary.ingredients || [];
    const total = ingredients.length;

    ingredients.forEach((ingredient) => {
      const calories = this.getIngredientEnergyValue(ingredient);

      if (calories < 250) {
        segments[0].count += 1;
        return;
      }

      if (calories < 500) {
        segments[1].count += 1;
        return;
      }

      if (calories < 1000) {
        segments[2].count += 1;
        return;
      }

      segments[3].count += 1;
    });

    const normalizedSegments = segments.map((segment) => ({
      label: segment.label,
      value: segment.count,
    }));

    if (!total) {
      return normalizedSegments.map((segment) => ({
        ...segment,
        percent: 0,
      }));
    }

    return this.buildPercentageSegments(normalizedSegments, total);
  }

  buildPercentageSegments(segments: Array<{ [key: string]: any }>, total: number) {
    if (!total || !segments.length) {
      return [];
    }

    const calculated: Array<{ [key: string]: any }> = segments.map((segment, index) => {
      const rawPercent = (segment.value / total) * 100;
      const basePercent = Math.floor(rawPercent);

      return {
        ...segment,
        index,
        rawPercent,
        percent: basePercent,
        remainder: rawPercent - basePercent,
      };
    });

    let remainingPercent = 100 - calculated.reduce((sum, segment) => sum + segment.percent, 0);

    const sortedSegments = calculated
      .slice()
      .sort((left, right) => right.remainder - left.remainder || left.index - right.index);

    for (let index = 0; index < sortedSegments.length && remainingPercent > 0; index += 1) {
      calculated[sortedSegments[index].index].percent += 1;
      remainingPercent -= 1;
    }

    return calculated.map((segment) => ({
      label: segment.label,
      value: segment.value,
      percent: segment.percent,
    }));
  }

  get ingredientUsageItems() {
    if (this.dashboardOverview?.ingredientUsage?.length) {
      const palette = ["#5aa04e", "#da8a33", "#5a90d6", "#a4b0bb", "#6c8f45", "#8a9bab"];
      const overviewItems = this.dashboardOverview.ingredientUsage;
      const maxCount = overviewItems[0]?.count || 1;

      return overviewItems.map((item, index) => ({
        ...item,
        share: Math.round((item.count / maxCount) * 100),
        color: palette[index] || palette[palette.length - 1],
      }));
    }

    return [];
  }

  get ingredientUsageTotal() {
    return this.ingredientUsageItems.reduce((sum, item) => sum + item.count, 0);
  }

  get ingredientUsageChartStyle() {
    const items = this.ingredientUsageItems;

    if (!items.length) {
      return {
        background: "#eef2f5",
      };
    }

    const total = this.ingredientUsageTotal || 1;
    let current = 0;
    const segments = items.map((item) => {
      const start = current;
      const portion = (item.count / total) * 360;
      current += portion;
      return `${item.color} ${start}deg ${current}deg`;
    });

    return {
      background: `conic-gradient(${segments.join(", ")})`,
    };
  }

  getAllergenCoverageChartStyle(item: { total: number; contained: number; analyzed: number }) {
    const total = item.total || 0;

    if (!total) {
      return {
        background: "#eef2f5",
      };
    }

    const containedDegrees = (item.contained / total) * 360;
    const analyzedOnlyDegrees = (Math.max(item.analyzed - item.contained, 0) / total) * 360;
    const pendingDegrees = Math.max(360 - containedDegrees - analyzedOnlyDegrees, 0);

    return {
      background: `conic-gradient(#5aa04e 0deg ${containedDegrees}deg, #9ec7de ${containedDegrees}deg ${containedDegrees + analyzedOnlyDegrees}deg, #e7edf1 ${containedDegrees + analyzedOnlyDegrees}deg ${containedDegrees + analyzedOnlyDegrees + pendingDegrees}deg)`,
    };
  }

  /**
   * Getter for grid headers. Done this way because headers are translatable and async
   * Used by both grids
   */
  get tableHeaders() {
    return [
      {
        name: this.translations.get("ingr_table_name"),
        key: "Name",
      },
      {
        name: this.translations.get("ingr_table_ene"),
        key: "Energy",
      },
      {
        name: this.translations.get("ingr_calories_from_fat") + " (g)",
        key: "CaloriesFromFat",
      },
      {
        name: this.translations.get("ingr_table_fat") + " (g)",
        key: "Fat",
      },
      {
        name: this.translations.get("ingr_table_sat") + " (g)",
        key: "SaturatedFattyAcids",
      },
      {
        name: this.translations.get("ingr_table_trans") + " (g)",
        key: "TransFattyAcids",
      },
      {
        name: this.translations.get("ingr_table_chol") + " (mg)",
        key: "Cholesterol",
      },
      {
        name: this.translations.get("ingr_table_sod") + " (mg)",
        key: "Sodium",
      },
      {
        name: this.translations.get("ingr_table_carb") + " (g)",
        key: "Carbohydrate",
      },
      {
        name: this.translations.get("ingr_table_fib") + " (g)",
        key: "Fibre",
      },
      {
        name: this.translations.get("ingr_table_sug") + " (g)",
        key: "Sugars",
      },
      {
        name: this.translations.get("ingr_table_prot") + " (g)",
        key: "Protein",
      },
      {
        name: this.translations.get("ingr_table_vitD") + " (mcg)",
        key: "VitaminD",
      },
      {
        name: this.translations.get("ingr_table_vitc") + " (mg)",
        key: "VitaminC",
      },
      {
        name: this.translations.get("ingr_table_pot") + " (mg)",
        key: "Potassium",
      },
      {
        name: this.translations.get("ingr_table_calc") + " (mg)",
        key: "Calcium",
      },
      {
        name: this.translations.get("ingr_table_iron") + " (mg)",
        key: "Iron",
      },
      {
        name: "Total",
        key: "Weight",
      },
    ];
  }

  get translations() {
    return this.localizationState.translations;
  }

  /**
   * Method for handling recipes fetching and setting recipes state
   */
  requestRecipes() {
    this.recentRecipes.loading = true;
    RecipesRepository.listRecipes(this.recentRecipes.listRequest)
      .then((response) => {
        this.recentRecipes.recipes = response.data.result;
        this.recentRecipes.recipes.map((recipe: any) => {
          recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        });
        this.recentRecipes.loading = false;

        if (this.recentRecipes.recipes.length > 0) {
          this.recentRecipes.hasRecipes = true;
        }
        else {
          this.recentRecipes.hasRecipes = false;
        }
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  getIngredientEnergyValue(ingredient: Ingredient) {
    const energyEntry = ingredient?.nutrients?.energy;
    const directValue = Number(energyEntry?.ingredientNutrient?.value);

    if (!Number.isNaN(directValue) && Number.isFinite(directValue)) {
      return directValue;
    }

    const nutrientEntries = Object.values(ingredient?.nutrients || {});
    const matchingEntry = nutrientEntries.find(({ ingredientNutrient }) => {
      return ingredientNutrient?.nutrientUnit?.nutrientType === NutrientType.Energy
        || ingredientNutrient?.nutrientId === NutrientType.Energy;
    });

    const fallbackValue = Number(matchingEntry?.ingredientNutrient?.value);
    return Number.isNaN(fallbackValue) || !Number.isFinite(fallbackValue) ? 0 : fallbackValue;
  }

  applyDashboardOverview(overview: DashboardOverviewState) {
    this.dashboardOverview = {
      ...(this.dashboardOverview || {}),
      ...(overview || {}),
    };

    const mergedOverview = this.dashboardOverview || {};
    this.totals = {
      totalRecipes: mergedOverview?.counts?.totalRecipes || 0,
      totalIngredients: mergedOverview?.counts?.totalCustomIngredients || 0,
      totalDishes: mergedOverview?.counts?.totalCombinedRecipes || 0,
      chartData: {
        lowCaloriesRecipes: mergedOverview?.caloriesBreakdown?.recipes?.[0]?.value || 0,
        moderateCaloriesRecipes: mergedOverview?.caloriesBreakdown?.recipes?.[1]?.value || 0,
        highCaloriesRecipes: mergedOverview?.caloriesBreakdown?.recipes?.[2]?.value || 0,
        overThousandCaloriesRecipes: mergedOverview?.caloriesBreakdown?.recipes?.[3]?.value || 0,
        lowCaloriesDishes: mergedOverview?.caloriesBreakdown?.combinedRecipes?.[0]?.value || 0,
        moderateCaloriesDishes: mergedOverview?.caloriesBreakdown?.combinedRecipes?.[1]?.value || 0,
        highCaloriesDishes: mergedOverview?.caloriesBreakdown?.combinedRecipes?.[2]?.value || 0,
        overThousandCaloriesDishes: mergedOverview?.caloriesBreakdown?.combinedRecipes?.[3]?.value || 0,
      },
    };
  }

  async loadDashboardOverview() {
    const userDetails = storeState.value.AccountModule.userDetails;
    const restaurantId = userDetails.restaurantId ?? null;
    const userId = userDetails.userId ?? null;
    const cache = loadDashboardOverviewCache<DashboardOverviewState>(restaurantId, userId);

    if (cache?.data) {
      this.applyDashboardOverview(cache.data);
    }

    const persistDashboardOverviewCache = () => {
      if (!this.dashboardOverview) {
        return;
      }

      saveDashboardOverviewCache({
        restaurantId,
        userId,
        cachedAt: new Date().toISOString(),
        data: this.dashboardOverview,
      });
    };

    const applyDashboardOverviewSegment = (overview: DashboardOverviewState) => {
      this.applyDashboardOverview(overview);
      persistDashboardOverviewCache();
    };

    const requests = [
      DashboardRepository.getCounts().then((response) => {
        applyDashboardOverviewSegment({
          counts: response?.data?.result,
        });
      }),
      DashboardRepository.getAllergenCoverage().then((response) => {
        applyDashboardOverviewSegment({
          allergenCoverage: response?.data?.result?.allergenCoverage || [],
        });
      }),
      DashboardRepository.getCaloriesBreakdown().then((response) => {
        applyDashboardOverviewSegment({
          caloriesBreakdown: response?.data?.result,
        });
      }),
      DashboardRepository.getUsageSummary().then((response) => {
        applyDashboardOverviewSegment({
          labelMaker: response?.data?.result?.labelMaker,
          ingredientUsage: response?.data?.result?.ingredientUsage || [],
        });
      }),
    ].map((request) => request.catch(() => {}));

    await Promise.allSettled(requests);
  }

  async loadRegulatoryCards() {
    try {
      const [regulatoryResponse, featureResponse] = await Promise.all([
        RegulatoryNotificationRepository.list("Regulatory", null, true),
        RegulatoryNotificationRepository.list("Feature"),
      ]);
      this.regulatoryItems = Array.isArray(regulatoryResponse.data?.result) ? regulatoryResponse.data.result : [];
      this.featureItems = Array.isArray(featureResponse.data?.result) ? featureResponse.data.result : [];
    }
    catch {
      this.regulatoryItems = [];
      this.featureItems = [];
    }
  }

  requestCustomIngredientSummary() {
    const totalIngredients = this.totals.totalIngredients || 0;

    if (!totalIngredients) {
      this.customIngredientSummary.ingredients = [];
      return;
    }

    this.customIngredientSummary.loading = true;

    const listRequest = new ListRequest({
      page: 1,
      pageSize: totalIngredients,
      filters: [],
      orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
    });

    IngredientsRepository.listRestaurantIngredients(listRequest)
      .then((response) => {
        this.customIngredientSummary.ingredients = Array.isArray(response.data?.result)
          ? response.data.result
          : [];
      })
      .catch(() => {
        this.customIngredientSummary.ingredients = [];
      })
      .finally(() => {
        this.customIngredientSummary.loading = false;
      });
  }

  loadAllergenRecipeSummary() {
    const totalRecipes = this.totals.totalRecipes || 0;

    if (!totalRecipes) {
      this.allergenSummary = {
        total: 0,
        analyzed: 0,
        contained: 0,
      };
      return;
    }

    const reducedRecipesRequest = new ListRequest({
      page: 1,
      pageSize: totalRecipes,
      orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
      filters: [],
    });

    RecipesRepository.listReducedRecipes(reducedRecipesRequest, true)
      .then((response: any) => {
        const items = response?.data?.result || response?.data || [];
        const analyzed = items.filter((item: any) => item?.hasAllergenAnalysis).length;
        const contained = items.filter((item: any) => item?.hasAllergenAnalysis && item?.hasAllergen).length;

        this.allergenSummary = {
          total: totalRecipes,
          analyzed,
          contained,
        };
      })
      .catch(() => {
        this.allergenSummary = {
          total: totalRecipes,
          analyzed: 0,
          contained: 0,
        };
      });
  }

  loadAllergenDishSummary() {
    const totalDishes = this.totals.totalDishes || 0;

    if (!totalDishes) {
      this.allergenDishSummary = {
        total: 0,
        analyzed: 0,
        contained: 0,
      };
      return;
    }

    const reducedDishesRequest = new ListRequest({
      page: 1,
      pageSize: totalDishes,
      orders: [{ field: "CreatedOn", orderType: OrderType.Desc }],
      filters: [],
    });

    RecipesRepository.listReducedRecipes(reducedDishesRequest, false)
      .then((response: any) => {
        const items = response?.data?.result || response?.data || [];
        const analyzed = items.filter((item: any) => item?.hasAllergenAnalysis).length;
        const contained = items.filter((item: any) => item?.hasAllergenAnalysis && item?.hasAllergen).length;

        this.allergenDishSummary = {
          total: totalDishes,
          analyzed,
          contained,
        };
      })
      .catch(() => {
        this.allergenDishSummary = {
          total: totalDishes,
          analyzed: 0,
          contained: 0,
        };
      });
  }

  /**
   * Method for handling recipes fetching and setting dishes state
   */
  requestDishes() {
    this.recentDishes.loading = true;
    DishesRepository.listDishes(this.recentDishes.listRequest)
      .then((response) => {
        this.recentDishes.dishes = response.data.result;
        this.recentDishes.dishes.map((dish: any) => {
          dish.nutritionAnalysis = mapNutritions(dish.nutritionAnalysis);
        });
        this.recentDishes.loading = false;

        if (this.recentDishes.dishes.length > 0) {
          this.recentDishes.hasDishes = true;
        }
        else {
          this.recentDishes.hasDishes = false;
        }
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  /**
   * Method for initializing both Chart.js charts if there are recipes / dishes in the system
   * https://www.chartjs.org/docs/latest/
   */
  initCharts() {
    try {
      if (this.totals.totalRecipes > 0) {
        // @ts-ignore
        const recipesCtx = document.getElementById("recipesChart").getContext("2d");
        let recipesChart = new Chart(recipesCtx, {
          type: "pie",
          data: {
            labels: ["Low Cal.", "Moderate Cal.", "High Cal."],
            datasets: [
              {
                label: "# of Votes",
                data: [
                  this.totals.chartData.lowCaloriesRecipes,
                  this.totals.chartData.moderateCaloriesRecipes,
                  this.totals.chartData.highCaloriesRecipes,
                ],
                backgroundColor: ["rgb(124, 179, 66)", "#d3bb00", "#b71c1c"],
                borderColor: ["#efebe9", "#efebe9", "#efebe9"],
              },
            ],
          },
          options: {
            animation: {
              duration: 3000,
            },
          },
        });
      }

      if (this.totals.totalDishes > 0) {
        // @ts-ignore
        const dishesCtx = document.getElementById("dishesChart").getContext("2d");
        let dishesChart = new Chart(dishesCtx, {
          type: "pie",
          data: {
            labels: ["Low Cal.", "Moderate Cal.", "High Cal."],
            datasets: [
              {
                label: "# of Votes",
                data: [
                  this.totals.chartData.lowCaloriesDishes,
                  this.totals.chartData.moderateCaloriesDishes,
                  this.totals.chartData.highCaloriesDishes,
                ],
                backgroundColor: ["rgb(124, 179, 66)", "#d3bb00", "#b71c1c"],
                borderColor: ["#efebe9", "#efebe9", "#efebe9"],
              },
            ],
          },
          options: {
            animation: {
              duration: 2000,
            },
          },
        });
      }
    }
    catch (error) {
      console.log(error);
    }
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["home", "ingredients", "recipes", "dishes", "common", "dashboard"],
      })
      .then(() => {
        // Set corresponding title to the page (Home | MenuSano)
        document.title =
          this.translations.get("dashboard") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });

    void this.loadTrialMetadata();
  }

  // Fetch dashboard data in mounted() hook because we need to make sure the DOM is loaded
  // and ready to be modified by initCharts();
  mounted() {
    this.handleStartupFeatureQuery();
    this.clearStartupFeatureQuery();

    this.loadDashboardOverview()
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.loadRegulatoryCards()
      .catch((error) => {
        if (error.response)
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

}
export default Home;
